import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Drawer, Form, Modal, Row, Space } from "antd";
import { ApartmentOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { Client } from "../../core/api-client";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { allPermissions } from "../../shared/constants/constants";
import { API_DEAL_STAGE_BASE, API_DEAL_SUB_STAGE_BASE, API_GET_ALL_DEAL_STAGES } from "../../shared/routes/private.routes";
import { RootState } from "../../shared/types/common";
import { accessAllowed, showNotification } from "../../shared/utils/utils";

const ManageDealStagesPage = ({ subStage = false }: { subStage?: boolean }) => {
  const title = subStage ? "Deal Sub-Stage" : "Deal Stage";
  const endpoint = subStage ? API_DEAL_SUB_STAGE_BASE : API_DEAL_STAGE_BASE;
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const canCreate = accessAllowed(myPermissions, subStage ? allPermissions.CREATE_DEAL_SUB_STAGE : allPermissions.CREATE_DEAL_STAGE);
  const canUpdate = accessAllowed(myPermissions, subStage ? allPermissions.UPDATE_DEAL_SUB_STAGE : allPermissions.UPDATE_DEAL_STAGE);
  const canDelete = accessAllowed(myPermissions, subStage ? allPermissions.DELETE_DEAL_SUB_STAGE : allPermissions.DELETE_DEAL_STAGE);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [rows, setRows] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [filterVisible, setFilterVisible] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const requestId = useRef(0);

  const loadList = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const response = await Client.post(`${endpoint}/list`, filters, { params: { page, limit: pageSize } });
      if (id !== requestId.current) return;
      if (!response || response.status < 200 || response.status >= 300) throw new Error("List request failed");
      const result = response.data;
      if (page > 1 && !result.data?.length && result.total > 0) {
        setPage(Math.max(1, Math.ceil(result.total / pageSize)));
        return;
      }
      setRows(result.data || []);
      setTotal(Number(result.total || 0));
    } catch {
      if (id === requestId.current) showNotification(`Unable to load ${title.toLowerCase()} records.`, "error");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [endpoint, filters, page, pageSize, title]);

  useEffect(() => {
    loadList();
    return () => { requestId.current += 1; };
  }, [loadList]);

  const loadStages = useCallback(async () => {
    if (!subStage) return;
    try {
      const response = await Client.get(API_GET_ALL_DEAL_STAGES);
      setStages(response?.data?.data || []);
    } catch {
      showNotification("Unable to load deal stages.", "error");
    }
  }, [subStage]);

  useEffect(() => { loadStages(); }, [loadStages]);

  const openEditor = (record: any = null) => {
    setSelected(record);
    form.resetFields();
    form.setFieldsValue(record ? {
      name: record.name, status: record.status, dealStageId: record.dealStageId,
    } : { status: "ACTIVE" });
    loadStages();
    setDrawerOpen(true);
  };

  const save = async (values: any) => {
    if (saving) return;
    setSaving(true);
    try {
      const body = { ...values, name: values.name.trim() };
      const response = selected
        ? await Client.put(`${endpoint}/update/${selected.id}`, body)
        : await Client.post(`${endpoint}/create`, body);
      if (response?.data?.statusCode !== 200) {
        const message = response?.data?.message;
        showNotification(Array.isArray(message) ? message.join(", ") : message || "Unable to save details.", "error");
        return;
      }
      showNotification(`${title} ${selected ? "updated" : "added"} successfully`, "success");
      setDrawerOpen(false);
      await loadList();
    } catch {
      showNotification("Unable to save details.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = (record: any) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `Delete "${record.name}"?`,
      okText: "Delete", okButtonProps: { danger: true },
      onOk: async () => {
        const response = await Client.delete(`${endpoint}/delete/${record.id}`);
        if (response?.data?.statusCode !== 200) {
          showNotification(response?.data?.message || "Unable to delete record.", "error");
          throw new Error("Delete rejected");
        }
        showNotification(`${title} deleted successfully`, "success");
        await loadList();
      },
    });
  };

  const stageOptions = stages
    .filter((stage) => stage.status === "ACTIVE")
    .map((stage) => ({ value: stage.id, label: stage.name }));
  const statusOptions = [{ value: "ACTIVE", label: "ACTIVE" }, { value: "INACTIVE", label: "INACTIVE" }];
  const closeEditor = () => { if (!saving) setDrawerOpen(false); };
  const clearFilters = () => { filterForm.resetFields(); setFilters({}); setPage(1); };
  const activeFilters = Object.keys(filters).filter((key) => isFilterValueActive(filters[key])).map((key) => ({
    key,
    fieldLabel: key === "dealStageId" ? "Deal Stage" : key === "status" ? "Status" : "Name",
    valueText: key === "dealStageId"
      ? stages.find((stage) => stage.id === filters[key])?.name || filters[key]
      : Array.isArray(filters[key]) ? filters[key].join(", ") : String(filters[key]),
  }));
  const filterProps = {
    form: filterForm, name: subStage ? "manage-deal-sub-stage-filters" : "manage-deal-stage-filters",
    initialValues: {}, enableEnterSubmit: true,
    formElements: [
      { name: "name", label: "", placeholder: `${title} Name Search`, type: "Input", colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 } },
      ...(subStage ? [{ name: "dealStageId", label: "", placeholder: "Deal Stage", type: "Select", showSearch: true, allowClear: true, options: stageOptions, colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 } }] : []),
      { name: "status", label: "", placeholder: "Status", type: "Select", showSearch: true, allowClear: true, mode: "multiple", options: statusOptions, colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 } },
    ],
    extraButtons: [
      { htmlType: "button", colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 }, type: "clearForm", submitButtonText: "Clear", onClick: clearFilters },
      { htmlType: "submit", colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 }, submitButtonText: "Apply", submitButtonType: "primary" },
    ],
    onFinish: (values: any) => { setPage(1); setFilters(values); }, onFinishFailed: () => {},
  };
  const editorProps = {
    form, name: subStage ? "create-edit-deal-sub-stage" : "create-edit-deal-stage",
    initialValues: { status: "ACTIVE" },
    formElements: [
      { name: "name", label: `${title} Name`, placeholder: `Enter ${title} Name`, type: "Input", colSpan: 24, allowClear: true, disabled: saving, rules: [{ required: true, whitespace: true, message: `Please enter a ${title.toLowerCase()} name` }] },
      ...(subStage ? [{ name: "dealStageId", label: "Deal Stage", placeholder: "Select Deal Stage", type: "Select", colSpanResponsive: { xs: 24, sm: 12 }, showSearch: true, allowClear: true, disabled: saving, options: stageOptions, rules: [{ required: true, message: "Please select a deal stage" }] }] : []),
      { name: "status", label: "Status", placeholder: "Status", type: "Select", colSpanResponsive: { xs: 24, sm: 12 }, showSearch: true, allowClear: true, disabled: saving, options: statusOptions, rules: [{ required: true, message: "Please select a status" }] },
    ],
    extraButtons: [
      { htmlType: "button", colSpan: 5, submitButtonText: "Cancel", onClick: closeEditor },
      { htmlType: "button", colSpan: 5, submitButtonText: "Clear", onClick: () => { if (!saving) form.resetFields(); } },
      { htmlType: "submit", colSpan: 5, loading: saving, submitButtonText: "Save", submitButtonType: "primary" },
    ],
    onFinish: save, onFinishFailed: () => {},
  };
  const columns: any[] = [
    { title: `${title} Name`, dataIndex: "name", key: "name" },
    ...(subStage ? [{ title: "Deal Stage", dataIndex: "dealStageName", key: "dealStageName" }] : []),
    { title: "Status", dataIndex: "status", key: "status" },
    ...((canUpdate || canDelete) ? [{
      title: "Actions", key: "actions", fixed: "right",
      render: (_: any, record: any) => <Space>
        {canUpdate && <Button type="link" icon={<EditOutlined />} title="Edit" aria-label="Edit" onClick={() => openEditor(record)} />}
        {canDelete && <Button type="link" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete" onClick={() => remove(record)} />}
      </Space>,
    }] : []),
  ];

  return <div className="app-page">
    <div className="app-page-header">
      <Row align="middle" justify="space-between" wrap>
        <Col flex="1" style={{ minWidth: 0 }}><div className="app-page-title-wrap">
          <div className="app-page-icon"><ApartmentOutlined /></div>
          <h1 className="app-page-title">Manage {title}</h1>
        </div></Col>
        <Col><Space size="middle" className="app-page-actions">
          {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor()}><span className="font-size-14">New {!screenType?.isPortrait && title}</span></Button>}
          <Button type="primary" shape="circle" icon={<BiFilterAlt />} title={filterVisible ? "Hide filters" : "Show filters"} aria-label="Filters" onClick={() => setFilterVisible(!filterVisible)} />
        </Space></Col>
      </Row>
    </div>
    {filterVisible && <div className="app-filter-bar">
      <div className="app-filter-header"><div className="app-filter-title"><BiFilterAlt style={{ fontSize: 14 }} />Filters</div>
        <Button type="text" size="small" icon={<CloseOutlined />} title="Close filters" aria-label="Close filters" onClick={() => setFilterVisible(false)} />
      </div>
      <div className="table-filter-form"><GenericFormComponent {...filterProps} /></div>
    </div>}
    <ActiveFilterChipsBar show={!filterVisible} entries={activeFilters} onClearAll={clearFilters} onRemove={(key) => {
      setFilters((previous: any) => { const next = { ...previous }; delete next[key]; return next; });
      filterForm.setFieldsValue({ [key]: key === "status" ? [] : undefined }); setPage(1);
    }} />
    <div className="app-table-card"><DataTableComponent rowKey="id" loading={loading} dataSource={rows} columns={columns}
      pagination defaultPage={1} currentPage={page} totalRows={total} pageSize={pageSize}
      onPageChange={setPage} onPageSizeChange={(_, size) => { setPageSize(size); setPage(1); }} />
    </div>
    <Drawer className="form-drawer" maskClosable={false} title={<strong>{`${selected ? "Edit" : "Add New"} ${title}`}</strong>}
      width={`min(${screenType?.isLg || screenType?.isXl ? 720 : 500}px, 90vw)`} open={drawerOpen} onClose={closeEditor}>
      <GenericFormComponent {...editorProps} />
    </Drawer>
  </div>;
};

export default ManageDealStagesPage;
